/**
 * Generato automaticamente.
 * procedimenti-authentication-manager
 */
import { useMutation } from 'react-query';
import type { MutationFunction, UseMutationOptions } from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  ArgumentsResponse,
  JNLPDocumentiRequest,
  JNLPFirmaRequest,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Effettua il download del file .jnlp per il jar di FirmaManager
 * @summary Firma Manager JNLP
 */
export const postJnlpFirmaManager = (
  jNLPFirmaRequest: JNLPFirmaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/procedimenti-authentication-manager/jnlp/firma-manager`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: jNLPFirmaRequest,
    },
    options,
  );
};

export const getPostJnlpFirmaManagerMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpFirmaManager>>,
    TError,
    { data: JNLPFirmaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJnlpFirmaManager>>,
  TError,
  { data: JNLPFirmaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJnlpFirmaManager>>,
    { data: JNLPFirmaRequest }
  > = props => {
    const { data } = props ?? {};

    return postJnlpFirmaManager(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJnlpFirmaManagerMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJnlpFirmaManager>>
>;
export type PostJnlpFirmaManagerMutationBody = JNLPFirmaRequest;
export type PostJnlpFirmaManagerMutationError = ErrorType<unknown>;

/**
 * @summary Firma Manager JNLP
 */
export const usePostJnlpFirmaManager = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpFirmaManager>>,
    TError,
    { data: JNLPFirmaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostJnlpFirmaManagerMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Genera la lista di arguments da passare al websocket di FirmaManager
 * @summary Firma Manager JNLP
 */
export const postJnlpFirmaManagerArguments = (
  jNLPFirmaRequest: JNLPFirmaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ArgumentsResponse>(
    {
      url: `/procedimenti-authentication-manager/jnlp/firma-manager-arguments`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: jNLPFirmaRequest,
    },
    options,
  );
};

export const getPostJnlpFirmaManagerArgumentsMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpFirmaManagerArguments>>,
    TError,
    { data: JNLPFirmaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJnlpFirmaManagerArguments>>,
  TError,
  { data: JNLPFirmaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJnlpFirmaManagerArguments>>,
    { data: JNLPFirmaRequest }
  > = props => {
    const { data } = props ?? {};

    return postJnlpFirmaManagerArguments(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJnlpFirmaManagerArgumentsMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJnlpFirmaManagerArguments>>
>;
export type PostJnlpFirmaManagerArgumentsMutationBody = JNLPFirmaRequest;
export type PostJnlpFirmaManagerArgumentsMutationError = ErrorType<unknown>;

/**
 * @summary Firma Manager JNLP
 */
export const usePostJnlpFirmaManagerArguments = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpFirmaManagerArguments>>,
    TError,
    { data: JNLPFirmaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostJnlpFirmaManagerArgumentsMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Effettua il download del file .jnlp per il jar di DocumentiManager
 * @summary Documenti Manager JNLP
 */
export const postJnlpDocumentiManager = (
  jNLPDocumentiRequest: JNLPDocumentiRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/procedimenti-authentication-manager/jnlp/documenti-manager`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: jNLPDocumentiRequest,
    },
    options,
  );
};

export const getPostJnlpDocumentiManagerMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpDocumentiManager>>,
    TError,
    { data: JNLPDocumentiRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJnlpDocumentiManager>>,
  TError,
  { data: JNLPDocumentiRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJnlpDocumentiManager>>,
    { data: JNLPDocumentiRequest }
  > = props => {
    const { data } = props ?? {};

    return postJnlpDocumentiManager(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJnlpDocumentiManagerMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJnlpDocumentiManager>>
>;
export type PostJnlpDocumentiManagerMutationBody = JNLPDocumentiRequest;
export type PostJnlpDocumentiManagerMutationError = ErrorType<unknown>;

/**
 * @summary Documenti Manager JNLP
 */
export const usePostJnlpDocumentiManager = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpDocumentiManager>>,
    TError,
    { data: JNLPDocumentiRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostJnlpDocumentiManagerMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Genera la lista di arguments da passare al websocket di DocumentiManager
 * @summary Documenti Manager JNLP
 */
export const postJnlpDocumentiManagerArguments = (
  jNLPDocumentiRequest: JNLPDocumentiRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ArgumentsResponse>(
    {
      url: `/procedimenti-authentication-manager/jnlp/documenti-manager-arguments`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: jNLPDocumentiRequest,
    },
    options,
  );
};

export const getPostJnlpDocumentiManagerArgumentsMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpDocumentiManagerArguments>>,
    TError,
    { data: JNLPDocumentiRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postJnlpDocumentiManagerArguments>>,
  TError,
  { data: JNLPDocumentiRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postJnlpDocumentiManagerArguments>>,
    { data: JNLPDocumentiRequest }
  > = props => {
    const { data } = props ?? {};

    return postJnlpDocumentiManagerArguments(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostJnlpDocumentiManagerArgumentsMutationResult = NonNullable<
  Awaited<ReturnType<typeof postJnlpDocumentiManagerArguments>>
>;
export type PostJnlpDocumentiManagerArgumentsMutationBody =
  JNLPDocumentiRequest;
export type PostJnlpDocumentiManagerArgumentsMutationError = ErrorType<unknown>;

/**
 * @summary Documenti Manager JNLP
 */
export const usePostJnlpDocumentiManagerArguments = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postJnlpDocumentiManagerArguments>>,
    TError,
    { data: JNLPDocumentiRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostJnlpDocumentiManagerArgumentsMutationOptions(options);

  return useMutation(mutationOptions);
};
